import { FC, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Flex, Input, TimePicker } from 'antd';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';

import Styles from './styles';
import { useToast } from '../../../../hooks/useToast';
import { useRecapManagement } from '../../../recapv2/recap-management/hook';
import { useVideoPlayer } from '../../../video/hooks';
import { coreService } from '../../../../services/core/core-service';
import { useClipEventStore } from '../../store/clip.store';

const { Container, FormItem, Label } = Styles;

const toDayjsTime = (seconds: number) => {
  const hour = Math.floor(seconds / 3600);
  const minute = Math.floor((seconds % 3600) / 60);
  const second = seconds % 60;
  return dayjs().hour(hour).minute(minute).second(second);
};

export const CreateClipV2: FC = () => {
  const { success, error } = useToast();
  const { id } = useRecapManagement();
  const { emit } = useClipEventStore();

  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const {
    setIsEditing,
    setCustomEditingMessage,
    setCropCallback,
    changeCropping,
    setNotifyOn,
    duration,
    setBlockActions,
  } = useVideoPlayer();

  const format = useMemo(() => {
    if (duration < 60) return 'ss';
    if (duration < 3600) return 'mm:ss';
    return 'HH:mm:ss';
  }, [duration]);

  const defaultTime = useMemo(() => dayjs('00:00:00', 'HH:mm:ss'), []);

  const max = useMemo(
    () => ({
      hours: Math.floor(duration / 3600),
      minutes: Math.floor((duration % 3600) / 60),
      seconds: duration % 60,
    }),
    [duration],
  );

  const min = useMemo(
    () => ({
      hours: Math.floor(startTime / 3600),
      minutes: Math.floor((startTime % 3600) / 60),
      seconds: startTime % 60,
    }),
    [startTime],
  );

  const handleClose = () => {
    setIsLoading(false);
    setIsEditing(false);
    setCustomEditingMessage('');
    setBlockActions(false);
  };

  useEffect(() => {
    setIsEditing(true);
    setCustomEditingMessage('Create new clip');
    setNotifyOn('change');
    setBlockActions(true);

    setCropCallback(({ start, end }) => {
      console.log(start, end);
      setStartTime(start);
      if (end > 0) setEndTime(end);
      return Promise.resolve();
    });

    return handleClose;
  }, []);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await coreService.createClip({
        eventId: id,
        title,
        startTime,
        endTime: Math.ceil(duration - endTime),
      });

      emit('changeTab', 'transcript');
      emit('refreshClips');
      success('Clip created successfully');
      handleClose();
    } catch (err) {
      error('Failed to create clip');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    emit('changeTab', 'transcript');
    handleClose();
  };

  const isFormInvalid = !title || startTime === endTime;

  const disabledTimeStart = () => ({
    disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h > max.hours),
    disabledMinutes: (hour: number) =>
      hour < max.hours ? [] : Array.from({ length: 60 }, (_, i) => i).filter(m => m > max.minutes),
    disabledSeconds: (hour: number, minute: number) =>
      hour < max.hours || minute < max.minutes
        ? []
        : Array.from({ length: 60 }, (_, i) => i).filter(s => s > max.seconds),
  });

  const disabledTimeEnd = () => ({
    disabledHours: () =>
      Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < min.hours || hour > max.hours),
    disabledMinutes: (hour: number) => {
      const all = Array.from({ length: 60 }, (_, i) => i);
      if (hour === min.hours && hour === max.hours) {
        return all.filter(m => m < min.minutes || m > max.minutes);
      }

      if (hour === min.hours) {
        return all.filter(m => m < min.minutes);
      }

      if (hour === max.hours) {
        return all.filter(m => m > max.minutes);
      }

      return [];
    },
    disabledSeconds: (hour: number, minute: number) => {
      const all = Array.from({ length: 60 }, (_, i) => i);

      if (hour === min.hours && minute === min.minutes) {
        return all.filter(s => s < min.seconds);
      }

      if (hour === max.hours && minute === max.minutes) {
        return all.filter(s => s > max.seconds);
      }

      return [];
    },
  });

  return (
    <Container>
      <Flex vertical gap={16}>
        <FormItem>
          <Label>Clip Title</Label>
          <Input placeholder="e.g. Some Title Here" onChange={e => setTitle(e.target.value)} />
        </FormItem>

        <Flex gap={8}>
          <FormItem style={{ width: '50%' }}>
            <Label>Start Time</Label>
            <TimePicker
              format={format}
              showNow={false}
              value={toDayjsTime(startTime)}
              defaultOpenValue={defaultTime}
              onChange={time =>
                time &&
                changeCropping({
                  start: time.hour() * 3600 + time.minute() * 60 + time.second(),
                  end: endTime,
                })
              }
              disabledTime={disabledTimeStart}
            />
          </FormItem>

          <FormItem style={{ width: '50%' }}>
            <Label>End Time</Label>
            <TimePicker
              format={format}
              value={
                duration - endTime === duration
                  ? toDayjsTime(duration - endTime)
                  : toDayjsTime(Math.ceil(duration) - endTime)
              }
              defaultOpenValue={defaultTime}
              showNow={false}
              onChange={time =>
                time &&
                changeCropping({
                  start: startTime,
                  end: duration - (time.hour() * 3600 + time.minute() * 60 + time.second()),
                })
              }
              disabledTime={disabledTimeEnd}
            />
          </FormItem>
        </Flex>

        <Flex gap={8} justify="end">
          <Button onClick={handleCancel} icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isFormInvalid}
          >
            Save Clip
          </Button>
        </Flex>
      </Flex>
    </Container>
  );
};
